import { FormBuilderContract } from '@/components/builder/form';
import { DateTime } from 'luxon';
import { SchemaManager } from '../../base/managers/SchemaManager';
import { DocumentManager } from './DocumentManager';
import { resolveEffectiveEntryData } from '../helpers/EffectiveEntryValueResolver';

// Blueprints
import { Blueprint } from '../../base/blueprints/Blueprint';
import { instanceOfVisibleBlueprint } from '../blueprints/VisibleBlueprint';
import { instanceOfReadonlyBlueprint } from '../blueprints/ReadonlyBlueprint';
import { instanceOfRequiredBlueprint } from '../blueprints/RequiredBlueprint';
import { CustomErrorBlueprint, instanceOfCustomErrorBlueprint } from '../blueprints/CustomErrorBlueprint';

// Enums
import { AlwaysChoice } from '../enums/AlwaysChoice';
import { InternallyChoice } from '../enums/InternallyChoice';
import { NeverChoice } from '../enums/NeverChoice';
import { WhenChoice } from '../enums/WhenChoice';

// Services
import { StateStore } from '../../base/services/StateStore';

// --------------------------------------------------

export class ExpressionManager
{
    private globals: any = null;
    private state: StateStore;
    private schema: SchemaManager;
    private document: DocumentManager;

    public constructor(state: StateStore, schemaManager: SchemaManager, documentManager: DocumentManager)
    {
        this.state = state;
        this.schema = schemaManager;
        this.document = documentManager;
    }

    private createEffectiveEntry(rootEntry: any, index?: number): any
    {
        if (!rootEntry || typeof rootEntry !== 'object')
            return rootEntry;

        // Podczas wyliczania defaultów nie używamy "effective Entry", żeby uniknąć rekurencji.
        const execute = (expression: string, rowIndex?: number): any => this.executeExpression(expression, rowIndex, false);
        const resolvedEntry: Record<string, any> = { ...rootEntry };

        for (const fieldName of Object.keys(rootEntry))
        {
            const entry = rootEntry[fieldName];

            if (!entry || typeof entry !== 'object')
            {
                continue;
            }

            if (entry.data !== null)
            {
                continue;
            }

            let blueprint = null;

            try
            {
                blueprint = this.schema.find(fieldName);
            }
            catch (e)
            {
                blueprint = null;
            }

            if (!blueprint || !('type' in blueprint))
            {
                continue;
            }

            resolvedEntry[fieldName] = {
                ...entry,
                data: resolveEffectiveEntryData({
                    blueprint,
                    currentData: entry.data,
                    executeExpression: execute,
                    index,
                }),
            };
        }

        return resolvedEntry;
    }

    private compileCode(expression: string, index?: number, choiceField?: (string | number)[], useEffectiveEntry: boolean = true): any
    {
        if (this.globals == null)
        {
            const validKey = new RegExp('^[a-zA-Z_$][0-9a-zA-Z_$]*$');

            this.globals = Object.keys(globalThis)
                .filter(k => validKey.test(k))
                .reduce((o, key) => ({ ...o, [key]: undefined }), {});
        }

        const variables = {
            globalThis: undefined,
            XMLHttpRequest: undefined,
            GeneratorFunction: undefined,
            AsyncFunction: undefined,
            Function: undefined,
            Object: undefined,
            console: undefined,
            eval: undefined,
            ...this.globals,
            DateTime: DateTime,
            Math: Math,
            Form: this.getProxy(),
            Entry: useEffectiveEntry ? this.createEffectiveEntry(this.document.getRootEntry(), index) : this.document.getRootEntry(),
            Schema: this.schema,
            Document: this.document,
            tableIndex: index,
        };

        // Dodatkowe sprawdzenie warunku na wypadek gdy zostanie zwrócona wartość false z ExpressionManager
        // W przypadku gdy zostanie zwrócona wartość false z ExpressionManager, sprawdzamy czy istnieje powiązane pole i sprawdzamy jeszcze raz warunek w ExpressionManager
        // przy 2 sprawdzeniu podmieniamy #additionalDataMode# na wartość klucza wybranej opcji
        if (choiceField && choiceField.length > 0)
        {
            choiceField.forEach((field) =>
            {
                setTimeout(() =>
                {
                    const data = variables.Entry[field].data;

                    if (data && data.length > 0 && data[0].includes("#additionalDataMode#"))
                    {
                        const key = data[0].replace("#additionalDataMode#", "");

                        const formData = variables.Form[field];
                        const options = formData.options;

                        const selectedOption = options.find((option: any) => option.value === key);

                        variables.Entry[field].data = [selectedOption.key];
                    }
                }, 1000);
            });
        }

        // eslint-disable-next-line no-new-func
        const code = new Function(...Object.keys(variables), `return (${expression})`);

        return code(...Object.values(variables));
    }

    private getProxy(): BlueprintManagerProxy
    {
        return new BlueprintManagerProxy(this.schema);
    }

    public checkExpression(expression: string): any
    {
        try
        {
            if (expression)
            {
                this.compileCode(expression);
            }

            return null;
        }
        catch (ex)
        {
            return ex;
        }
    }

    public executeExpression(expression: string, index?: number, useEffectiveEntry: boolean = true): any
    {
        try
        {
            if (expression)
            {
                return this.compileCode(expression, index, undefined, useEffectiveEntry);
            }
        }
        catch (ex) { /**/ }

        return null;
    }

    public executeExpressionWithChoiceFieldData(expression: string, choiceField: (string | number)[] = [], index?: number): any
    {
        try
        {
            if (expression)
            {
                return this.compileCode(expression, index, choiceField);
            }
        }
        catch (ex) { /**/ }

        return null;
    }

    public visible(blueprint: Blueprint, ancestors: boolean = false, choiceField: (string | number)[] = [], tableRowIndex: number = null): boolean
    {
        let result = true;

        if (blueprint && instanceOfVisibleBlueprint(blueprint))
        {

            if (this.state.design)
            {
                result = blueprint.visible == AlwaysChoice.Always;
            }
            else
            {
                switch (blueprint.visible)
                {
                    case AlwaysChoice.Always:
                        result = true;
                        break;
                    case NeverChoice.Never:
                        result = false;
                        break;
                    case InternallyChoice.Internally:
                        result = this.state.internal;
                        break;
                    case WhenChoice.When:
                        if (choiceField.length > 0)
                        {
                            result = this.executeExpressionWithChoiceFieldData(blueprint.visibleWhen, choiceField);
                        }
                        else
                        {
                            result = this.executeExpression(blueprint.visibleWhen, tableRowIndex);
                        }

                        result = result !== null ? result : true;
                        break;
                }
            }
        }

        if (ancestors == true)
        {
            do
            {
                blueprint = this.schema.parent(blueprint);
                result = result && this.visible(blueprint);
            }
            while (blueprint != null);
        }

        return result;
    }

    public readonly(blueprint: Blueprint, ancestors: boolean = false, index: number = null): boolean
    {
        let result = false;

        if (blueprint && instanceOfReadonlyBlueprint(blueprint))
        {
            if (this.state.design)
            {
                result = blueprint.readonly != NeverChoice.Never;
            }
            else
            {
                switch (blueprint.readonly)
                {
                    case AlwaysChoice.Always:
                        result = true;
                        break;
                    case NeverChoice.Never:
                        result = false;
                        break;
                    case InternallyChoice.Internally:
                        result = this.state.internal;
                        break;
                    case WhenChoice.When:
                        result = this.executeExpression(blueprint.readonlyWhen, index);
                        result = result !== null ? result : true;
                        break;
                }
            }
        }

        if (ancestors == true)
        {
            do
            {
                blueprint = this.schema.parent(blueprint);
                result = result || this.readonly(blueprint);
            }
            while (blueprint != null);
        }

        return result;
    }

    public required(blueprint: Blueprint, index: number = null): boolean
    {
        let result = false;

        if (instanceOfRequiredBlueprint(blueprint))
        {
            if (this.state.design)
            {
                result = blueprint.required != NeverChoice.Never;
            }
            else
            {
                switch (blueprint.required)
                {
                    case AlwaysChoice.Always:
                        result = true;
                        break;
                    case NeverChoice.Never:
                        result = false;
                        break;
                    case WhenChoice.When:
                        result = this.executeExpression(blueprint.requiredWhen, index);
                        result = result !== null ? result : true;
                        break;
                }
            }
        }

        return result;
    }

    public customError(blueprint: CustomErrorBlueprint): boolean
    {
        if (instanceOfCustomErrorBlueprint(blueprint) && blueprint.customError == WhenChoice.When)
        {
            return this.executeExpression(blueprint.customErrorWhen);
        }

        return false;
    }

    public customErrorMessage(blueprint: CustomErrorBlueprint, form: FormBuilderContract): string
    {
        if (instanceOfCustomErrorBlueprint(blueprint) && blueprint.customError == WhenChoice.When)
        {
            return this.executeExpression("`" + form.localization.translate(blueprint.customErrorMessage) + "`") || '[[[Podano nieprawidłowe dane]]]';
        }

        return null;
    }
}

export class BlueprintManagerProxy
{
    public constructor(manager: SchemaManager)
    {
        return new Proxy(manager, {
            get: (manager: any, property: string) =>
            {
                return (manager as SchemaManager).find(property);
            }
        });
    }
}
